// client/src/components/ActivityTimeline.js
// A contact's interactions, newest first: type, subject, mandate and date.
// Shared by the contact and partner detail modals and the profile page.
//
// When the contact's own addresses are passed (`emails`), each email row
// also says which of them it went through ("from" or "to/cc"). A person can
// have several addresses (e.g. after merging duplicate profiles); the
// interaction keeps its raw sender/to/cc, so this is always recoverable.
import { useQuery } from '@tanstack/react-query'
import api from '../lib/api'

const INT_TYPE_BADGE = {
  'Email': 'badge-blue', 'Meeting': 'badge-purple',
  'Call': 'badge-green', 'Note': 'badge-gray', 'Event': 'badge-yellow',
}

// Which of the contact's addresses this interaction used, and how.
function addressesUsed(i, emails) {
  const mine = new Set(emails.map(e => e.toLowerCase()))
  if (!mine.size) return null
  const sender = (i.sender_email ?? '').toLowerCase()
  const others = [...(i.to_emails ?? []), ...(i.cc_emails ?? [])].map(e => e.toLowerCase())
  if (mine.has(sender)) return { how: 'from', addresses: [sender] }
  const to = [...new Set(others.filter(e => mine.has(e)))]
  return to.length ? { how: 'to', addresses: to } : null
}

function formatDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function ActivityTimeline({ contactId, emails = [] }) {
  const { data: activity = [], isLoading } = useQuery({
    queryKey: ['interactions', { contact_id: contactId }],
    queryFn: () => api.get('/api/interactions', { params: { contact_id: contactId } }).then(r => r.data.data ?? []),
  })

  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase',
        letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 10 }}>
        Activity Timeline
      </div>
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
          <div className="spinner" />
        </div>
      ) : activity.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '12px 0' }}>
          No interactions recorded yet.
        </div>
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
          {activity.map((i, idx) => (
            <div key={i.interaction_id} style={{
              padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10,
              borderBottom: idx < activity.length - 1 ? '1px solid var(--border)' : 'none',
            }}>
              <span className={`badge ${INT_TYPE_BADGE[i.type] ?? 'badge-gray'}`} style={{ flexShrink: 0 }}>
                {i.type}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden',
                  textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {i.subject_title || '(no subject)'}
                </div>
                {i.mandate && (
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 1 }}>
                    {i.mandate.mandate_name}
                  </div>
                )}
                {(() => {
                  const used = addressesUsed(i, emails)
                  return used && (
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>
                      {used.how === 'from' ? 'From ' : 'To/cc '}{used.addresses.join(', ')}
                    </div>
                  )
                })()}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', flexShrink: 0 }}>
                {formatDate(i.date)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
